import React, { Suspense, lazy, useState, useEffect, useRef, Component, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppContext } from './contexts/AppContext';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useTheme } from './hooks/useTheme';
import { useMuteList } from './hooks/useMuteList';
import Layout from './components/Layout';
import LoginDialog from './components/LoginDialog';
import UnlockDialog from './components/UnlockDialog';
import { useNostrPublish } from './hooks/useNostrPublish';
import {
  getStoredReferrer, clearStoredReferrer,
  hasConfirmedReferral, markReferralConfirmed,
  REFERRAL_KIND, REFERRAL_DRTAG,
} from './lib/referral';
import { initPush } from './lib/pushNotifications';
import { startPushHeartbeat } from './lib/pushHeartbeat';
import { fetchDeletionsForUser } from './components/PostCard';
import { RadioPlayerProvider } from './contexts/RadioPlayerContext';
import { initBanSystem, processBanEvents, FLAG_KIND, BAN_TAG, UNBAN_TAG } from './lib/communityFlag';
import { useSharedTurnConfig } from './hooks/useSharedTurnConfig';
import { OPERATOR_PUBKEY } from './lib/platform';

// ── Global Error Boundary ────────────────────────────────────────
// Catches ANY uncaught React render error and shows a recovery UI
// instead of a blank white screen. The user can clear corrupted
// local data and reload, or just reload directly.

interface ErrorBoundaryProps { children: ReactNode; level?: 'app' | 'page' }
interface ErrorBoundaryState { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    try {
      // Clear potentially corrupted data — but preserve login credentials
      // (WIF keys, nsec, bunker config) so the user doesn't lose their wallet
      const preserve = [
        'bchnostr:current-user', 'bchnostr:login-method',
      ];
      // Also preserve any tip wallet WIF keys
      const preserveKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          preserve.includes(key) ||
          key.startsWith('bchnostr:tip-wallet-wif') ||
          key.startsWith('bchnostr:nsec') ||
          // Business Page keys + their registry — losing these means losing
          // the business identity permanently, so they must survive a reset.
          key.startsWith('bchnostr:biz-nsec:') ||
          key === 'bchnostr:business-identities' ||
          key === 'bchnostr:owner-session'
        )) {
          preserveKeys.push(key);
        }
      }
      const saved: Record<string, string> = {};
      preserveKeys.forEach(k => { const v = localStorage.getItem(k); if (v) saved[k] = v; });

      // Clear everything
      localStorage.clear();
      sessionStorage.clear();

      // Restore preserved keys
      Object.entries(saved).forEach(([k, v]) => localStorage.setItem(k, v));
    } catch {}
    window.location.href = '/';
  };

  handleFullReset = () => {
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isPage = this.props.level === 'page';
    const errorMsg = this.state.error?.message || 'Unknown error';

    // Page-level: show inline recovery with Clear Cache as primary action
    if (isPage) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif', maxWidth: '440px', margin: '0 auto' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Something went wrong</div>
          <p style={{ color: '#888', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: 1.5 }}>
            This is usually caused by corrupted cached data. Clearing the cache will fix it.
            Your wallet and login are safe.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
            <button
              onClick={this.handleClearAndReload}
              style={{ padding: '0.7rem 1.5rem', borderRadius: '0.5rem', background: 'linear-gradient(135deg, #10b981, #0d9488)', color: 'white', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Clear Cache &amp; Reload
            </button>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button
                onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); }}
                style={{ padding: '0.5rem 1.25rem', borderRadius: '0.5rem', background: '#333', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleFullReset}
                style={{ padding: '0.5rem 1.25rem', borderRadius: '0.5rem', background: 'none', color: '#888', border: '1px solid #444', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Full Reset
              </button>
            </div>
          </div>
          <div style={{ background: '#1a1a1a', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'left', border: '1px solid #333' }}>
            <p style={{ fontSize: '0.7rem', color: '#888', marginBottom: '0.25rem' }}>Error (share this with admin if the problem persists):</p>
            <pre style={{ fontSize: '0.7rem', color: '#f87171', overflow: 'auto', maxHeight: '80px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{errorMsg}{this.state.errorInfo?.componentStack ? '\n' + this.state.errorInfo.componentStack.split('\n').slice(0, 5).join('\n') : ''}</pre>
          </div>
        </div>
      );
    }

    // App-level: full-screen recovery
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif', padding: '1.5rem',
        background: '#0f0f0f', color: '#e5e5e5',
      }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>BCHNostr</div>
          <div style={{
            width: '3rem', height: '3px', margin: '0 auto 1.5rem',
            background: 'linear-gradient(90deg, #10b981, #0d9488)',
            borderRadius: '2px',
          }} />

          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#a3a3a3', lineHeight: 1.5, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            The app hit an unexpected error. This can usually be fixed by clearing cached data.
            Your wallet and login are safe.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '0.75rem', borderRadius: '0.75rem', border: '1px solid #333',
                background: '#1a1a1a', color: '#e5e5e5', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              Reload App
            </button>
            <button
              onClick={this.handleClearAndReload}
              style={{
                padding: '0.75rem', borderRadius: '0.75rem', border: 'none',
                background: 'linear-gradient(135deg, #10b981, #0d9488)', color: 'white',
                fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              Clear Cache &amp; Reload
            </button>
            <button
              onClick={this.handleFullReset}
              style={{
                padding: '0.5rem', background: 'none', border: 'none',
                color: '#737373', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              Full Reset (clears everything including login)
            </button>
          </div>

          <details style={{ marginTop: '2rem', textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer', color: '#737373', fontSize: '0.75rem' }}>
              Technical details
            </summary>
            <pre style={{
              fontSize: '0.7rem', padding: '0.75rem', background: '#1a1a1a',
              color: '#f87171', borderRadius: '0.5rem', overflow: 'auto',
              maxHeight: '120px', marginTop: '0.5rem', border: '1px solid #262626',
            }}>
              {errorMsg}
              {this.state.errorInfo?.componentStack?.slice(0, 500)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

const Index = lazy(() => import('./pages/Index'));
const Explore = lazy(() => import('./pages/Explore'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BCHLivePage = lazy(() => import('./pages/BCHLivePage'));
const LiveStreamPage = lazy(() => import('./pages/LiveStreamPage'));
const CreatorStudioPage = lazy(() => import('./pages/CreatorStudioPage'));
const MerchantDirectoryPage = lazy(() => import('./pages/MerchantDirectoryPage'));
const TipManagerPage = lazy(() => import('./pages/TipManagerPage'));

const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ExchangePage = lazy(() => import('./pages/ExchangePage'));
const SwapPage = lazy(() => import('./pages/SwapPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const PollsPage = lazy(() => import('./pages/PollsPage'));
const TimeCapsulesPage = lazy(() => import('./pages/TimeCapsulesPage'));

const BCHRoomsPage = lazy(() => import('./pages/BCHRoomsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotePage = lazy(() => import('./pages/NotePage'));
const TokenPage = lazy(() => import('./pages/TokenPage'));
const TokensPage = lazy(() => import('./pages/TokensPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const FaucetPage = lazy(() => import('./pages/FaucetPage'));
const CrowdfundPage = lazy(() => import('./pages/CrowdfundPage'));
const CreatorSupportPage = lazy(() => import('./pages/CreatorSupportPage'));
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'));

const BlogPage = lazy(() => import('./pages/BlogPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const BlogComposer = lazy(() => import('./pages/BlogComposer'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const StampsPage = lazy(() => import('./pages/StampsPage'));
const CommunitiesPage = lazy(() => import('./pages/CommunitiesPage'));
const CommunityViewPage = lazy(() => import('./pages/CommunityViewPage'));
const CashClipsPage = lazy(() => import('./pages/CashClipsPage'));
const ClipsTestPage = lazy(() => import('./pages/ClipsTestPage'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const VerifiedPage = lazy(() => import('./pages/VerifiedPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const ChessPage = lazy(() => import('./pages/ChessPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const PacManPage = lazy(() => import('./pages/PacManPage'));
const WordlePage = lazy(() => import('./pages/WordlePage'));
const PokerPage = lazy(() => import('./pages/PokerPage'));
const RadioPage = lazy(() => import('./pages/RadioPage'));
const PodcastsPage = lazy(() => import('./pages/PodcastsPage'));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const EpisodePage = lazy(() => import('./pages/EpisodePage'));
const PodcastSeedPage = lazy(() => import('./pages/PodcastSeedPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const VideoWatchPage = lazy(() => import('./pages/VideoWatchPage'));
const VideoChannelPage = lazy(() => import('./pages/VideoChannelPage'));
const VideoPlaylistPage = lazy(() => import('./pages/VideoPlaylistPage'));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const MyImpactPage = lazy(() => import('./pages/MyImpactPage'));
const EmbedWidgetPage = lazy(() => import('./pages/EmbedWidgetPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60000, refetchOnWindowFocus: false } },
});

function Loading() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

// ── Scroll restoration ────────────────────────────────────────────
// Keyed by React Router's `location.key` — every history entry gets
// a unique key, so going back/forward restores the right position
// even if the same pathname appears multiple times in history.
//
// We also persist `visibleCount` (how many posts the user loaded in
// the feed) so that on return we render enough items for the page to
// be tall enough to scroll to the saved position.

// Let the browser handle scroll restoration natively for back/forward.
// We augment it with our own logic only when native restoration fails
// (e.g. because async content hasn't rendered yet).
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'auto';
}

const SCROLL_KEY = 'bchnostr:scroll:';
const VISIBLE_KEY = 'bchnostr:visible:';

/** Save scroll Y for a given history key (called frequently). */
function saveScrollForKey(key: string) {
  try { sessionStorage.setItem(SCROLL_KEY + key, String(window.scrollY)); } catch {}
}

/** Read saved scroll Y for a given history key. */
function getSavedScroll(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY + key);
    return raw !== null ? parseInt(raw, 10) : null;
  } catch { return null; }
}

/** Save visibleCount for a pathname so it persists across navigations. */
export function saveVisibleCount(pathname: string, count: number) {
  try { sessionStorage.setItem(VISIBLE_KEY + pathname, String(count)); } catch {}
}

/** Read saved visibleCount for a pathname. */
export function getSavedVisibleCount(pathname: string): number | null {
  try {
    const raw = sessionStorage.getItem(VISIBLE_KEY + pathname);
    return raw !== null ? parseInt(raw, 10) : null;
  } catch { return null; }
}

const FEED_TYPE_KEY = 'bchnostr:feedType:';

/** Save feedType for a pathname so returning via back button lands on the same tab. */
export function saveFeedType(pathname: string, feedType: string) {
  try { sessionStorage.setItem(FEED_TYPE_KEY + pathname, feedType); } catch {}
}

/** Read saved feedType for a pathname. */
export function getSavedFeedType(pathname: string): string | null {
  try { return sessionStorage.getItem(FEED_TYPE_KEY + pathname); } catch { return null; }
}

// ── Referral confirmation on login ─────────────────────────────────
// If the user arrived via a referral link (?ref=<pubkey>), confirm
// the referral as soon as they log in — don't wait for their first
// post. This ensures ambassadors get credit even if the referred user
// just browses without posting.
//
// IMPORTANT: We only clear the stored referrer and mark as confirmed
// AFTER the publish succeeds. If it fails, we retry on next render
// cycle. This prevents lost referrals from network/signer issues.
function ReferralConfirmer({ userPubkey }: { userPubkey: string | undefined }) {
  const { publish } = useNostrPublish();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!userPubkey || firedRef.current) return;
    if (hasConfirmedReferral()) return;

    const referrer = getStoredReferrer();
    if (!referrer || referrer === userPubkey) return; // can't refer yourself

    firedRef.current = true;

    // Publish with retry — don't clear referrer until confirmed
    const attemptPublish = async (attempt: number) => {
      // Wait for relay connections to establish (longer on first attempt)
      const delay = attempt === 0 ? 4000 : 8000 * attempt;
      await new Promise(r => setTimeout(r, delay));

      try {
        await publish({
          kind: REFERRAL_KIND,
          content: '',
          tags: [
            ['d', REFERRAL_DRTAG],
            ['ref', referrer],
            ['p', referrer],
            ['action', 'signup'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
        // Success — NOW we can safely mark as confirmed
        markReferralConfirmed();
        clearStoredReferrer();
        console.log('[Referral] Confirmed referral from', referrer.slice(0, 8));
      } catch (err) {
        console.warn(`[Referral] Publish attempt ${attempt + 1} failed:`, err);
        if (attempt < 2) {
          // Retry up to 3 times total
          attemptPublish(attempt + 1);
        } else {
          // All retries failed — DON'T mark as confirmed.
          // firedRef prevents re-firing this render cycle, but on next
          // page load it will try again since localStorage isn't cleared.
          console.error('[Referral] All publish attempts failed — will retry on next visit');
          firedRef.current = false;
        }
      }
    };

    attemptPublish(0);
  }, [userPubkey]);

  return null;
}

// ── Ban list loader ─────────────────────────────────────────────
// Fetches operator-authored ban/unban events from relays on startup
// so banned users' content is hidden for all visitors.
/**
 * Loads the operator's published TURN relay config so BCH Rooms can
 * connect users whose networks block direct peer-to-peer connections.
 * Renders nothing; the config is cached for `getIceServers()` to read.
 */
function SharedTurnLoader() {
  useSharedTurnConfig();
  return null;
}

function BanListLoader() {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    initBanSystem();

    // Fetch ban events from relays using raw WebSocket
    // (We can't use useNostrQuery here because this needs to run before
    // any content renders, and it only needs the operator's events)
    const relays = [
      'wss://nos.lol',
      'wss://relay.primal.net',
      'wss://relay.nostr.net',
    ];

    for (const url of relays) {
      try {
        const ws = new WebSocket(url);
        const subId = 'ban_' + Math.random().toString(36).slice(2, 8);
        const timeout = setTimeout(() => { try { ws.close(); } catch {} }, 10000);
        const events: any[] = [];

        ws.onopen = () => {
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [FLAG_KIND],
            authors: [OPERATOR_PUBKEY],
            '#t': [BAN_TAG],
            limit: 500,
          }, {
            kinds: [FLAG_KIND],
            authors: [OPERATOR_PUBKEY],
            '#t': [UNBAN_TAG],
            limit: 100,
          }]));
        };

        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[1] === subId && data[2]) {
              events.push(data[2]);
            }
            if (data[0] === 'EOSE') {
              processBanEvents(events);
              clearTimeout(timeout);
              try { ws.close(); } catch {}
            }
          } catch {}
        };

        ws.onerror = () => clearTimeout(timeout);
      } catch {}
    }
  }, []);

  return null;
}

function ScrollRestorer() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevKeyRef = useRef<string | null>(null);

  // ── Save scroll position continuously ──
  useEffect(() => {
    const key = location.key;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          saveScrollForKey(key);
          ticking = false;
        });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      saveScrollForKey(key);
    };
  }, [location.key]);

  // ── Handle navigation ──
  useEffect(() => {
    const key = location.key;

    if (prevKeyRef.current === null) {
      prevKeyRef.current = key;
      return;
    }
    prevKeyRef.current = key;

    if (navType === 'POP') {
      const savedY = getSavedScroll(key);
      if (savedY !== null && savedY > 0) {
        let cancelled = false;
        let succeeded = false;

        const tryScroll = () => {
          if (cancelled || succeeded) return;
          // Only act if the browser's native restoration failed
          // (i.e. we're still near the top when we should be lower)
          if (Math.abs(window.scrollY - savedY) <= 5) {
            succeeded = true;
            return;
          }
          window.scrollTo({ top: savedY, behavior: 'instant' as ScrollBehavior });
          if (Math.abs(window.scrollY - savedY) <= 5) {
            succeeded = true;
          }
        };

        // Check after increasing delays — gives the browser a chance
        // to restore natively first, then we step in as fallback
        const DELAYS = [50, 100, 200, 350, 500, 800, 1200, 1800, 2500, 3500, 5000];
        DELAYS.forEach(delay => {
          setTimeout(() => { if (!cancelled && !succeeded) tryScroll(); }, delay);
        });

        // Also try whenever DOM changes (posts rendering in)
        const observer = new MutationObserver(() => {
          if (!cancelled && !succeeded) requestAnimationFrame(tryScroll);
        });
        observer.observe(document.body, { childList: true, subtree: true });

        const cleanup = setTimeout(() => observer.disconnect(), 8000);

        return () => {
          cancelled = true;
          observer.disconnect();
          clearTimeout(cleanup);
        };
      }
    } else {
      // PUSH or REPLACE — new forward navigation, scroll to top
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [location.key, navType]);

  return null;
}

export default function App() {
  const { user, login, loginWithExtension, loginWithNsec, loginWithBunker, logout, updateProfile, switchToBusiness, switchToPersonal, showLoginDialog, setShowLoginDialog, nsecLocked, lockedPubkey, unlockWithPin } = useCurrentUser();
  const { theme, toggleTheme } = useTheme();
  const { mutedPubkeys, mute, unmute, isMuted } = useMuteList();

  // Register push service worker for returning users
  useEffect(() => {
    initPush();
  }, []);

  // Drive the push dispatcher while the app is open (client heartbeat).
  // Netlify cron registration is unreliable via our deploy path, so this
  // guarantees notifications flow whenever anyone is using the site.
  useEffect(() => {
    startPushHeartbeat();
  }, []);

  // Fetch user's deletion events so deleted posts stay hidden across reloads
  useEffect(() => {
    if (user?.pubkey) fetchDeletionsForUser(user.pubkey);
  }, [user?.pubkey]);

  return (
    <ErrorBoundary level="app">
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={{
        user, login, loginWithExtension, loginWithNsec, loginWithBunker, logout, updateProfile,
        switchToBusiness, switchToPersonal,
        showLoginDialog, setShowLoginDialog,
        nsecLocked, lockedPubkey, unlockWithPin,
        theme, toggleTheme, mutedPubkeys, mute, unmute, isMuted,
      }}>
        <RadioPlayerProvider>
        <BrowserRouter>
          <ScrollRestorer />
          <BanListLoader />
          <SharedTurnLoader />
          <ReferralConfirmer userPubkey={user?.pubkey} />
          <Routes>
            {/* ── Embeddable widgets ──────────────────────────────────────
                Chrome-free, loaded inside an <iframe> on external sites. Kept
                OUTSIDE the Layout route so there is no sidebar / app shell —
                just the widget. Read-only and login-free. Shows a creator's
                Creator Support goal, keyed on their npub. */}
            <Route path="/embed/goal/:npub" element={<ErrorBoundary level="page"><Suspense fallback={null}><EmbedWidgetPage /></Suspense></ErrorBoundary>} />

            <Route element={<Layout />}>
              <Route index element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><Index /></Suspense></ErrorBoundary>} />
              <Route path="/explore" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><Explore /></Suspense></ErrorBoundary>} />
              <Route path="/search" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><SearchPage /></Suspense></ErrorBoundary>} />
              <Route path="/settings" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><SettingsPage /></Suspense></ErrorBoundary>} />
              <Route path="/live" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BCHLivePage /></Suspense></ErrorBoundary>} />
              <Route path="/live/pk/:pubkey" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><LiveStreamPage /></Suspense></ErrorBoundary>} />
              <Route path="/live/:username" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><LiveStreamPage /></Suspense></ErrorBoundary>} />
              <Route path="/creator/live" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CreatorStudioPage /></Suspense></ErrorBoundary>} />
              <Route path="/merchants" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><MerchantDirectoryPage /></Suspense></ErrorBoundary>} />
              <Route path="/tips" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><TipManagerPage /></Suspense></ErrorBoundary>} />

              <Route path="/messages" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><MessagesPage /></Suspense></ErrorBoundary>} />
              <Route path="/exchange" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ExchangePage /></Suspense></ErrorBoundary>} />
              <Route path="/swap" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><SwapPage /></Suspense></ErrorBoundary>} />
              <Route path="/ecosystem" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><EcosystemPage /></Suspense></ErrorBoundary>} />
              <Route path="/leaderboard" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><LeaderboardPage /></Suspense></ErrorBoundary>} />
              <Route path="/marketplace" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><MarketplacePage /></Suspense></ErrorBoundary>} />
              <Route path="/polls" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PollsPage /></Suspense></ErrorBoundary>} />
              <Route path="/capsules" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><TimeCapsulesPage /></Suspense></ErrorBoundary>} />

              <Route path="/rooms" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BCHRoomsPage /></Suspense></ErrorBoundary>} />
              {/* Old /spaces URLs — BCH Rooms is now the P2P audio surface */}
              <Route path="/spaces" element={<Navigate to="/rooms" replace />} />
              <Route path="/profile/:pubkey" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ProfilePage /></Suspense></ErrorBoundary>} />
              <Route path="/note/:noteId" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><NotePage /></Suspense></ErrorBoundary>} />
              <Route path="/tokens" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><TokensPage /></Suspense></ErrorBoundary>} />
              <Route path="/token/:id" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><TokenPage /></Suspense></ErrorBoundary>} />
              <Route path="/faq" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><FAQPage /></Suspense></ErrorBoundary>} />
              <Route path="/about" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><AboutPage /></Suspense></ErrorBoundary>} />
              <Route path="/faucet" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><FaucetPage /></Suspense></ErrorBoundary>} />
              <Route path="/crowdfund" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CrowdfundPage /></Suspense></ErrorBoundary>} />
              <Route path="/support" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CreatorSupportPage /></Suspense></ErrorBoundary>} />
              <Route path="/bookmarks" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BookmarksPage /></Suspense></ErrorBoundary>} />
              <Route path="/stamps" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><StampsPage /></Suspense></ErrorBoundary>} />
              <Route path="/communities" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CommunitiesPage /></Suspense></ErrorBoundary>} />
              <Route path="/community/:communityId" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CommunityViewPage /></Suspense></ErrorBoundary>} />
              <Route path="/notifications" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><NotificationsPage /></Suspense></ErrorBoundary>} />
              <Route path="/clips" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><CashClipsPage /></Suspense></ErrorBoundary>} />
              <Route path="/clips-test" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ClipsTestPage /></Suspense></ErrorBoundary>} />
              <Route path="/metrics" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><MetricsPage /></Suspense></ErrorBoundary>} />
              <Route path="/jobs" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><JobsPage /></Suspense></ErrorBoundary>} />
              <Route path="/verified" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><VerifiedPage /></Suspense></ErrorBoundary>} />
              <Route path="/feedback" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><FeedbackPage /></Suspense></ErrorBoundary>} />
              <Route path="/chess" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ChessPage /></Suspense></ErrorBoundary>} />
              <Route path="/games" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><GamesPage /></Suspense></ErrorBoundary>} />
              <Route path="/games/pacman" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PacManPage /></Suspense></ErrorBoundary>} />
              <Route path="/games/wordle" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><WordlePage /></Suspense></ErrorBoundary>} />
              <Route path="/games/poker" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PokerPage /></Suspense></ErrorBoundary>} />
              <Route path="/radio" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><RadioPage /></Suspense></ErrorBoundary>} />
              {/* Podcasts — discovery, one show page, one page per episode.
                  Each gets its own page-level ErrorBoundary so a hostile or
                  malformed RSS feed can only ever break its own page. */}
              <Route path="/podcasts" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PodcastsPage /></Suspense></ErrorBoundary>} />
              {/* Operator-only seeding tool — populates the directory with
                  existing shows, each under its own generated identity. */}
              <Route path="/podcasts/seed" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PodcastSeedPage /></Suspense></ErrorBoundary>} />
              <Route path="/podcasts/:pubkey" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><PodcastPage /></Suspense></ErrorBoundary>} />
              <Route path="/podcasts/:pubkey/:slug" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><EpisodePage /></Suspense></ErrorBoundary>} />
              {/* BCH Video — discovery, one watch page, creator channels, playlists.
                  BCHNostr hosts no video: every player points at the external
                  provider. Each route gets its own page-level ErrorBoundary so a
                  broken external embed can only ever break its own page. */}
              <Route path="/videos" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><VideosPage /></Suspense></ErrorBoundary>} />
              <Route path="/videos/watch/:pubkey/:dTag" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><VideoWatchPage /></Suspense></ErrorBoundary>} />
              <Route path="/videos/channel/:pubkey" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><VideoChannelPage /></Suspense></ErrorBoundary>} />
              <Route path="/videos/playlist/:pubkey/:dTag" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><VideoPlaylistPage /></Suspense></ErrorBoundary>} />
              <Route path="/activities" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ActivitiesPage /></Suspense></ErrorBoundary>} />
              <Route path="/events" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><EventsPage /></Suspense></ErrorBoundary>} />
              <Route path="/impact" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><MyImpactPage /></Suspense></ErrorBoundary>} />
              {/* ── BCH Ink (long-form publishing) ──────────────────────────
                  Canonical routes live under /ink. The original /blog routes
                  are kept as permanent aliases so every existing shared link,
                  bookmark, and indexed URL keeps resolving to the same article.
                  Both paths render the exact same components over the exact
                  same kind 30023 data — no migration, no duplication. */}
              <Route path="/ink" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BlogPage /></Suspense></ErrorBoundary>} />
              <Route path="/ink/write" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BlogComposer /></Suspense></ErrorBoundary>} />
              <Route path="/ink/:naddr" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ArticlePage /></Suspense></ErrorBoundary>} />
              {/* Legacy aliases — MUST keep working */}
              <Route path="/blog" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BlogPage /></Suspense></ErrorBoundary>} />
              <Route path="/blog/write" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><BlogComposer /></Suspense></ErrorBoundary>} />
              <Route path="/blog/:naddr" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><ArticlePage /></Suspense></ErrorBoundary>} />

              <Route path="*" element={<ErrorBoundary level="page"><Suspense fallback={<Loading />}><NotFound /></Suspense></ErrorBoundary>} />
            </Route>
          </Routes>
          {/* Login Dialog — shown when no extension available */}
          <LoginDialog />
          {/* Unlock Dialog — shown when an encrypted nsec is stored but locked */}
          <UnlockDialog />
        </BrowserRouter>
        </RadioPlayerProvider>
      </AppContext.Provider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

